import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  InputAdornment,
  Chip,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import CalculateIcon from '@mui/icons-material/Calculate';
import apiService from './api'; // Your API service
import TransactionWarningDialog from './warning';

const Edit = ({ 
  open, 
  onClose, 
  transactionData, 
  onConfirm 
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  
  // State for allocation entries
  const [allocations, setAllocations] = useState([]);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [totalTransaction, setTotalTransaction] = useState(0);

  // Selected member for current allocation
  const [selectedMember, setSelectedMember] = useState(null);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningData, setWarningData] = useState(null);

  useEffect(() => {
    if (open && transactionData) {
      // Reset allocation data
      setAllocations([]);
      setSelectedMember(null);
      setAllocationAmount('');
      setError(null);
      
      // Set initial remaining amount equal to transaction total
      const amount = parseFloat(transactionData.amount || 0);
      setRemainingAmount(amount);
      setTotalTransaction(amount);
      setShowWarningDialog(false); // NEW
      setWarningData(null); 
      
      // Fetch member list for selection
      fetchMembers();
    }
  }, [open, transactionData]);

  const fetchMembers = async () => {
    setLoadingMembers(true);
    setError(null);
    try {
      console.log('Fetching members...');
      const data = await apiService.getMemberSelection();
      console.log('Members fetched:', data);
      setMembers(data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(`Error fetching members: ${err.message}`);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleAddAllocation = () => {
    if (!selectedMember || !allocationAmount || parseFloat(allocationAmount) <= 0) {
      setError('Please select a member and enter a valid amount');
      return;
    }

    const amount = parseFloat(allocationAmount);
    
    // Check if amount exceeds remaining balance
    if (amount > remainingAmount) {
      setError(`Amount exceeds remaining balance of KES ${remainingAmount.toFixed(2)}`);
      return;
    }
    
    // Check if member already has an allocation
    const existingAllocationIndex = allocations.findIndex(
      allocation => allocation.member.id === selectedMember.id
    );
    
    // Create a new allocations array
    let newAllocations = [...allocations];
    
    if (existingAllocationIndex >= 0) {
      // Update existing allocation
      const oldAmount = newAllocations[existingAllocationIndex].amount;
      newAllocations[existingAllocationIndex] = {
        ...newAllocations[existingAllocationIndex],
        amount: oldAmount + amount
      };
    } else {
      // Add new allocation
      newAllocations.push({
        member: selectedMember,
        amount: amount
      });
    }
    
    // Update state
    setAllocations(newAllocations);
    setRemainingAmount(prev => parseFloat((prev - amount).toFixed(2)));
    setSelectedMember(null);
    setAllocationAmount('');
    setError(null);
  };

  const handleRemoveAllocation = (index) => {
    const allocation = allocations[index];
    setRemainingAmount(prev => parseFloat((prev + allocation.amount).toFixed(2)));
    
    const newAllocations = [...allocations];
    newAllocations.splice(index, 1);
    setAllocations(newAllocations);
  };

  const handleFullAmount = () => {
    if (!selectedMember) {
      setError('Please select a member first');
      return;
    }
    
    setAllocationAmount(remainingAmount.toString());
  };

  const handleConfirm = async () => {
    if (allocations.length === 0) {
      setError('Please add at least one allocation');
      return;
    }
  
    if (remainingAmount > 0.01) {
      setError(`Please allocate the remaining KES ${remainingAmount.toFixed(2)}`);
      return;
    }
    
    // NEW: Check for warnings before proceeding
    if (!showWarningDialog && !warningData) {
      try {
        setLoading(true);
        const distributions = allocations.map(allocation => ({
          member_id: allocation.member.id,
          amount: allocation.amount
        }));
        
        const warningCheck = await apiService.checkTransactionWarning(
          transactionData.transaction_id || transactionData.mpesaRef,
          distributions
        );
        
        if (warningCheck.has_warnings) {
          setWarningData(warningCheck.warnings);
          setShowWarningDialog(true);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Warning check failed:', err);
        // Continue with distribution even if warning check fails
      }
    }
    
    // Proceed with distribution
    setLoading(true);
    setError(null);
    
    try {
      console.log('Confirming payment distribution...');
      console.log('Transaction Data:', transactionData);
      console.log('Allocations:', allocations);
      
      const distributionData = {
        transaction_id: transactionData.transaction_id || transactionData.mpesaRef,
        allocations: allocations.map(allocation => ({
          member_id: allocation.member.id,
          amount: allocation.amount
        }))
      };
      
      console.log('Distribution Data:', distributionData);
      
      const response = await apiService.distributePayment(distributionData);
      console.log('Distribution response:', response);
      
      if (onConfirm) {
        onConfirm(response);
      }
      
      onClose();
      
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError(`Failed to confirm payment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleWarningProceed = () => {
    setShowWarningDialog(false);
    setWarningData(null);
    // Continue with distribution
    handleConfirm();
  };
  
  const handleWarningCancel = () => {
    setShowWarningDialog(false);
    setWarningData(null);
  };
  // Calculate totals for display
  const totalAllocated = allocations.reduce((sum, allocation) => sum + allocation.amount, 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}    
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        bgcolor: '#2196f3', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">
            Edit Payment Allocation
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {/* Transaction Details Section */}
        <Box sx={{ 
          bgcolor: 'background.default', 
          p: 2, 
          borderRadius: 1,
          mb: 3
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Transaction Details
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <Box sx={{ mr: 4, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Transaction ID:</Typography>
              <Typography variant="body1">
                {transactionData?.transaction_id || transactionData?.mpesaRef || 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ mr: 4, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Amount:</Typography>
              <Typography variant="body1">KES {totalTransaction.toFixed(2)}</Typography>
            </Box>
            
            <Box sx={{ mr: 4, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Phone:</Typography>
              <Typography variant="body1">
                {transactionData?.phone_number || transactionData?.phoneNo || 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Date:</Typography>
              <Typography variant="body1">
                {transactionData?.transaction_time || transactionData?.time || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Payment Calculator/Distribution Section */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Paper sx={{ flex: 1, p: 2, bgcolor: 'background.default', border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Payment Distribution
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Total Amount:</Typography>
              <Typography variant="body1" fontWeight="bold">
                KES {totalTransaction.toFixed(2)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Allocated:</Typography>
              <Typography variant="body1">
                KES {totalAllocated.toFixed(2)}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold">Remaining:</Typography>
              <Typography 
                variant="body1" 
                fontWeight="bold"
                color={remainingAmount === 0 ? 'success.main' : 'warning.main'}
              >
                KES {remainingAmount.toFixed(2)}
              </Typography>
            </Box>
          </Paper>
          
          {/* Add New Allocation Form */}
          <Paper sx={{ flex: 2, p: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Add Allocation
            </Typography>
            
            {loadingMembers ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Autocomplete
                    options={members || []}
                    getOptionLabel={(option) => `${option.name} (${option.groupName})`}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1">{option.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.groupName}, ID: {option.id}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    value={selectedMember}
                    onChange={(event, newValue) => setSelectedMember(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Member"
                        variant="outlined"
                        fullWidth
                        size="small"
                      />
                    )}
                    sx={{ flex: 2 }}
                  />
                  
                  <TextField
                    label="Amount"
                    value={allocationAmount}
                    onChange={(e) => setAllocationAmount(e.target.value)}
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">KES</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            size="small" 
                            onClick={handleFullAmount}
                            title="Use full remaining amount"
                          >
                            <CalculateIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    size="small"
                    sx={{ flex: 1.7, minWidth: '180px' }}
                  />
                  
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddAllocation}
                    startIcon={<AddIcon />}
                    disabled={!selectedMember || !allocationAmount || parseFloat(allocationAmount) <= 0}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
        
        {/* Allocations List */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Payment Allocations ({allocations.length})
        </Typography>
        
        <Paper sx={{ mb: 2, maxHeight: '200px', overflow: 'auto' }}>
          {allocations.length > 0 ? (
            <List>
              {allocations.map((allocation, index) => (
                <React.Fragment key={`${allocation.member.id}-${index}`}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      <PersonIcon color="primary" fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={allocation.member.name}
                      secondary={
                        <React.Fragment>
                          <Typography component="span" variant="body2" color="text.primary">
                            {allocation.member.groupName}
                          </Typography>
                          {" — ID: " + allocation.member.id}
                        </React.Fragment>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      <Chip 
                        label={`KES ${allocation.amount.toFixed(2)}`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleRemoveAllocation(index)}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No allocations added yet. Add members to distribute the payment.
              </Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          CANCEL
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleConfirm}
          disabled={loading || allocations.length === 0}
          startIcon={loading && <CircularProgress size={18} color="inherit" />}
        >
          {loading ? 'PROCESSING...' : 'CONFIRM PAYMENT DISTRIBUTION'}
        </Button>
      </DialogActions>
      {/* Warning Dialog */}
      <TransactionWarningDialog
        open={showWarningDialog}
        onClose={handleWarningCancel}
        onProceed={handleWarningProceed}
        warnings={warningData}
        transactionType="distribution"
        loading={loading}
      />
    </Dialog>
  );
};

export default Edit;